// profiles-load — a CARGA dos perfis da conta pras telas de perfil (TV e mobile): o
// pull de perfis (conclusivo) + a leitura local, com o desfecho que a decisão pura
// (profiles-gate.ts) precisa. Uma fonte só pras duas superfícies: o mobile não puxava
// perfil nenhum (lia só o local) e a TV decidia sem saber se o pull tinha dado certo.

import { getProfilesForAccount, type Profile } from './local-db'
import { orderAccountFirst, type ProfilesPullState } from './profiles-gate'
import { ensureProfilesPulled, hasSwipeSession, profilesPulledThisSession } from './sync'

export interface AccountProfilesLoad {
  /** Perfis da conta primeiro, depois os só-locais (ver `orderAccountFirst`). */
  profiles: Profile[]
  /** `pending` nunca sai daqui: a carga termina sempre num estado conclusivo, e NUNCA
   *  rejeita (pull que rejeita vira `failed`; leitura local ilegível vira lista vazia). */
  pull: Exclude<ProfilesPullState, 'pending'>
  /** Perfis vivos da conta (quando `pull === 'ok'`). */
  remoteIds: ReadonlySet<string> | null
}

/**
 * Puxa os perfis da conta e lê a lista local. Com o pull já feito nesta sessão, uma
 * falha do pull de AGORA não rebaixa a tela pra "tentar novamente": a lista que ela já
 * mostra é a da conta (ver `profilesPulledThisSession`).
 */
export async function loadAccountProfiles(account: string): Promise<AccountProfilesLoad> {
  if (!hasSwipeSession()) {
    return { profiles: await readLocal(account), pull: 'local', remoteIds: null }
  }
  let outcome: Awaited<ReturnType<typeof ensureProfilesPulled>>
  try {
    outcome = await ensureProfilesPulled()
  } catch {
    // O pull REJEITOU (ex.: o IDB recusou gravar um perfil adotado: quota no
    // `applyRemoteProfile`). Antes isto subia pra tela, que ficava em "Carregando" pra
    // sempre (code-review, rodada 5). É falha do pull como outra qualquer: "tentar
    // novamente" com a lista local.
    outcome = { status: 'failed' }
  }
  const local = await readLocal(account)
  if (outcome.status === 'ok') {
    return { profiles: orderAccountFirst(local, outcome.remoteIds), pull: 'ok', remoteIds: outcome.remoteIds }
  }
  if (outcome.status === 'failed') {
    const known = profilesPulledThisSession(account)
    if (known) return { profiles: orderAccountFirst(local, known), pull: 'ok', remoteIds: known }
    return { profiles: local, pull: 'failed', remoteIds: null }
  }
  // `no-context` (lista direta: não sincroniza) e `no-session` (a sessão caiu no meio):
  // a lista local é a verdade.
  return { profiles: local, pull: 'local', remoteIds: null }
}

/** A leitura local, que também não pode derrubar a carga: IDB ilegível = lista vazia
 *  (a tela segue no estado que o pull decidir; o "tentar novamente" refaz as duas). */
async function readLocal(account: string): Promise<Profile[]> {
  try {
    return await getProfilesForAccount(account)
  } catch {
    return []
  }
}
